/** Local, deterministic passage comparison. Never a plagiarism or AI verdict. */
export const COMPARISON_LIMITS = { documents: 20, characters: 40000, totalCharacters: 240000, matchesPerPair: 100, operationsPerPair: 500000 };
export const ORAL_QUESTIONS = [
  'Peux-tu expliquer ce passage avec tes propres mots ?',
  'Comment as-tu construit ce raisonnement ? Quelles étapes as-tu suivies ?',
  'Quelles sources as-tu utilisées et comment les as-tu choisies ?',
  'Peux-tu donner un autre exemple qui illustre cette idée ?',
];
const normalize = word => word.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('fr');
export function tokenize(text) {
  return [...text.matchAll(/[\p{L}\p{N}]+/gu)].map(m => ({ word: normalize(m[0]), start: m.index, end: m.index + m[0].length }));
}
const keyAt = (tokens, i, n) => tokens.slice(i, i + n).map(t => t.word).join(' ');
export function prepareText(text, { minimumWords = 8, excludeQuotes = true, reference = '' } = {}) {
  const tokens = tokenize(text);
  const excluded = new Set();
  if (excludeQuotes) {
    const ranges = [...text.matchAll(/«[\s\S]*?»|“[\s\S]*?”|"[\s\S]*?"/g)].map(m => [m.index, m.index + m[0].length]);
    tokens.forEach((t, i) => { if (ranges.some(([a, b]) => t.start >= a && t.end <= b)) excluded.add(i); });
  }
  const ref = tokenize(reference);
  const keys = new Set();
  for (let i = 0; i <= ref.length - minimumWords; i++) keys.add(keyAt(ref, i, minimumWords));
  if (keys.size) for (let i = 0; i <= tokens.length - minimumWords; i++) {
    if (keys.has(keyAt(tokens, i, minimumWords))) for (let j = i; j < i + minimumWords; j++) excluded.add(j);
  }
  return { text, tokens, excluded, wordCount: tokens.length, excludedWords: excluded.size };
}
const validWindow = (doc, i, n) => { for (let j = i; j < i + n; j++) if (doc.excluded.has(j)) return false; return true; };
export function comparePair(a, b, minimumWords = 8) {
  const index = new Map();
  for (let j = 0; j <= b.tokens.length - minimumWords; j++) {
    if (!validWindow(b, j, minimumWords)) continue;
    const key = keyAt(b.tokens, j, minimumWords);
    if (!index.has(key)) index.set(key, []);
    index.get(key).push(j);
  }
  const matches = [];
  let operations = 0, truncated = false;
  const equal = (i, j) => i >= 0 && j >= 0 && i < a.tokens.length && j < b.tokens.length && !a.excluded.has(i) && !b.excluded.has(j) && a.tokens[i].word === b.tokens[j].word;
  outer: for (let i = 0; i <= a.tokens.length - minimumWords; i++) {
    if (!validWindow(a, i, minimumWords)) continue;
    for (const j of index.get(keyAt(a.tokens, i, minimumWords)) || []) {
      if (++operations > COMPARISON_LIMITS.operationsPerPair) { truncated = true; break outer; }
      // A maximal run is emitted only at its start; never join separated passages.
      if (equal(i - 1, j - 1)) continue;
      let length = minimumWords;
      while (equal(i + length, j + length)) {
        if (++operations > COMPARISON_LIMITS.operationsPerPair) { truncated = true; break outer; }
        length++;
      }
      const aStart = a.tokens[i].start, aEnd = a.tokens[i + length - 1].end;
      const bStart = b.tokens[j].start, bEnd = b.tokens[j + length - 1].end;
      matches.push({ words: length, aStart, aEnd, bStart, bEnd, aExcerpt: a.text.slice(aStart, Math.min(aEnd, aStart + 1600)) + (aEnd - aStart > 1600 ? '… [extrait abrégé]' : ''), bExcerpt: b.text.slice(bStart, Math.min(bEnd, bStart + 1600)) + (bEnd - bStart > 1600 ? '… [extrait abrégé]' : '') });
      if (matches.length >= COMPARISON_LIMITS.matchesPerPair) { truncated = true; break outer; }
    }
  }
  return { matches, truncated };
}
export function validateDocuments(documents) {
  if (documents.length < 2 || documents.length > COMPARISON_LIMITS.documents) throw new Error('Ajoutez entre 2 et 20 textes.');
  if (documents.some(d => typeof d.text !== 'string' || !d.text.trim() || d.text.length > COMPARISON_LIMITS.characters)) throw new Error('Chaque texte doit contenir entre 1 et 40 000 caractères.');
  if (documents.reduce((n, d) => n + d.text.length, 0) > COMPARISON_LIMITS.totalCharacters) throw new Error('La session est limitée à 240 000 caractères.');
}
export function findMarker(text, marker) {
  if (!marker) return [];
  const positions = [];
  let i = text.indexOf(marker);
  while (i !== -1) { positions.push(i); i = text.indexOf(marker, i + marker.length); }
  return positions;
}
export async function compareDocuments(documents, options = {}, { signal, onProgress } = {}) {
  validateDocuments(documents);
  const minimumWords = Number(options.minimumWords ?? 8);
  if (!Number.isInteger(minimumWords) || minimumWords < 6 || minimumWords > 12) throw new Error('Le seuil doit être compris entre 6 et 12 mots.');
  if ((options.reference || '').length > COMPARISON_LIMITS.characters) throw new Error('La référence est limitée à 40 000 caractères.');
  const checkAbort = () => { if (signal?.aborted) throw new DOMException('Comparaison annulée.', 'AbortError'); };
  checkAbort();
  const prepared = documents.map(d => ({ ...prepareText(d.text, { ...options, minimumWords }), alias: d.alias }));
  const pairs = [], total = documents.length * (documents.length - 1) / 2;
  let completed = 0;
  for (let i = 0; i < prepared.length; i++) for (let j = i + 1; j < prepared.length; j++) {
    await new Promise(resolve => setTimeout(resolve, 0));
    checkAbort();
    const result = comparePair(prepared[i], prepared[j], minimumWords);
    pairs.push({ a: i, b: j, ...result });
    onProgress?.(++completed / total);
  }
  checkAbort();
  return { minimumWords, excludeQuotes: options.excludeQuotes !== false, referenceProvided: !!options.reference?.trim(), pairs, documents: prepared.map((d, i) => ({ alias: d.alias, wordCount: d.wordCount, excludedWords: d.excludedWords, markerPositions: findMarker(documents[i].text, options.marker || '') })) };
}
export function reportText(result) {
  const lines = ['Copiveo — Relevé local de passages communs', 'Ce relevé ne permet pas de conclure à une fraude ou à un recours à une IA.', `Seuil : ${result.minimumWords} mots. Casse, accents et ponctuation ignorés.`, `Citations entre guillemets exclues : ${result.excludeQuotes ? 'oui' : 'non'}. Référence fournie : ${result.referenceProvided ? 'oui' : 'non'}.`, 'Les reformulations, sources web et textes non déposés ne sont pas recherchés.', ''];
  result.documents.forEach(d => lines.push(`${d.alias} : ${d.wordCount} mots, ${d.excludedWords} mots exclus ; marqueur exact : ${d.markerPositions.length} occurrence(s).`));
  for (const pair of result.pairs) {
    lines.push('', `${result.documents[pair.a].alias} / ${result.documents[pair.b].alias} : ${pair.matches.length} passage(s) commun(s)${pair.truncated ? ' ; résultat partiel (limite atteinte)' : ''}`);
    pair.matches.forEach(m => lines.push(`${m.words} mots — texte A, caractères ${m.aStart + 1}–${m.aEnd} : ${m.aExcerpt}`, `Texte B, caractères ${m.bStart + 1}–${m.bEnd} : ${m.bExcerpt}`));
  }
  lines.push('', 'Une occurrence du marqueur peut venir d’un copier-coller ou d’un lecteur d’écran. Ce n’est pas une preuve.', '', 'Questions possibles pour un échange oral :', ...ORAL_QUESTIONS.map(q => `- ${q}`));
  return lines.join('\n');
}
